import { z } from "zod";

declare module "zod" {
  interface ZodType<Output = any, Def = z.ZodTypeDef, Input = Output> {
    split(value?: boolean): this;
  }
}

(z.ZodType as any).prototype.split = function (value: boolean = true) {
  this._def.ui = { ...(this._def.ui ?? {}), split: value };
  return this;
};

export const TermMetaSchema = z
  .object({
    // Core Hour Thresholds
    over_time_hours: z.enum(["40"]).optional().describe("Hours per week after which overtime is applied").split(),
    daily_overtime_threshold: z
      .enum(["10"])
      .optional()
      .describe("Hours each day after which time and a half is applied up until 14 hours")
      .split(),
    daily_doubletime_threshold: z
      .enum(["14"])
      .optional()
      .describe("Hours each day after which double time is applied to the rest of the shift")
      .split(),
    minimum_hours: z.enum(["2", "4", "6", "8"]).optional().describe("Minimum hours per standard shift").split(),
    rigging_minimum_hours: z
      .enum(["6"])
      .optional()
      .describe(
        "Minimum hours for riggers. Applies to first shift of the day only. Standard minimum hours apply after that.",
      )
      .split(),
    shift_differential_preferred: z
      .enum(["1.25"])
      .optional()
      .describe("Shift differential applied to preferred customers")
      .split(),
    shift_differential_regular: z
      .enum(["1.5"])
      .optional()
      .describe("Shift differential applied to standard customers")
      .split(),
    meal_penalty_hours: z.enum(["5"]).optional().describe("Hours before meal penalty applies"),
    meal_penalty_rate: z
      .enum(["1.5"])
      .optional()
      .describe("Rate applied to hours after when a meal penalty is incurred")
      .split(),
    meal_callback_minimum: z
      .enum(["2"])
      .optional()
      .describe(
        "Guaranteed hours after a meal break, e.g. if the crew only works an hour after coming back from a meal break they would get paid for two hours. This applies to billing as well.",
      )
      .split(),
    meal_break_duration: z.enum(["1"]).optional().describe("Minimum meal duration for non billable meal breaks"),
    crew_short_cancelation: z
      .enum(["4"])
      .optional()
      .describe("Hours for which crew is paid and client is billed if a shift is cancled within 24 hour or less notice")
      .split(),
    rigging_short_cancelation: z
      .enum(["1"])
      .optional()
      .describe(
        "Rigging shifts canceled with 24 hour or less notice are paid and billed for the projected length of the shift or rigging_mimimum_hours (whicherver is greater) at this rate",
      )
      .split(),
    rigging_two_day_cancelation: z
      .enum(["0.5"])
      .optional()
      .describe(
        "Rigging shifts canceled bewteen 24 and 48 hours of the shift start time are paid and billed for the projected length of the shift or rigging_mimimum_hours (whichever is greater) at this rate",
      )
      .split(),

    preferred_partner_discount: z
      .enum(["0.05"])
      .optional()
      .describe("Percentage discount for preferred partners")
      .split(),
    early_scheduling_limit: z.enum(["10"]).optional().describe("Limit for early scheduling discount to apply").split(),
    early_scheduling_percentage: z
      .enum(["0.09"])
      .optional()
      .describe("Preferred partner early scheduling discount")
      .split(),
    premium_holiday_rate: z
      .enum(["2"])
      .optional()
      .describe("Holiday pay rate for Thanksgiving Day, Christmas Eve, and Christmas Day")
      .split(),
    other_holiday_rate: z
      .enum(["1.5"])
      .optional()
      .describe(
        "Holiday pay rate for New Year's Eve, New Year's Day, MLK Day, President's Day, Easter Sunday, Memorial Day, Juneteenth, Independence Day, Labor Day, Columbus Day, and Veterans Day",
      )
      .split(),
    late_scheduling_fee: z
      .enum(["35"])
      .optional()
      .describe(
        "Flat dollar fee per crew member added or removed at client request with less notice than the schduling notice minimum",
      )
      .split(),
    late_scheduling_limit: z
      .enum(["2"])
      .optional()
      .describe("Shifts scheduled with less notice than this have the late_scheduling_fee applied to each position"),
    billing_net: z
      .enum(["10", "20", "30"])
      .optional()
      .describe(
        "Standard due date for payments after an invoice is sent to a client. Payments made after this have the late_payment_penalty applied",
      ),
    late_payment_penalty: z
      .enum(["0.05"])
      .optional()
      .describe("Additional percentage of toatl bill charged for late payments"),
  })
  .describe(
    "Employment terms and scheduling preferences for shifts and billing. Supports generic, union, client-specific, and regional/state law compliance.",
  );

// Adjudication schemas for projects (which store terms with source tracking)
export const TermSourceSchema = z.enum(["tenant", "client", "project"]);
export type TermSource = z.infer<typeof TermSourceSchema>;

// Non-split adjudicated value with source tracking
export const AdjudicatedTermValueSchema = z.object({
  value: z.any(), // Can be number, string, boolean, enum from TermMetaSchema
  source: TermSourceSchema,
});

// Split adjudicated value with separate billable/payable tracking
export const SplitAdjudicatedTermValueSchema = z.object({
  billable: z.object({
    value: z.any(),
    source: TermSourceSchema,
  }),
  payable: z.object({
    value: z.any(),
    source: TermSourceSchema,
  }),
});

// Union type - adjudicated term can be either split or non-split
export const AdjudicatedTermSchema = z.union([AdjudicatedTermValueSchema, SplitAdjudicatedTermValueSchema]);

// Record of adjudicated terms (used by projects)
export const AdjudicatedTermsSchema = z.record(z.string(), AdjudicatedTermSchema).optional();

// Field groupings for UI organization
export const TermFieldGroups: Record<string, string[]> = {
  "Overtime & Hour Thresholds": [
    "over_time_hours",
    "daily_overtime_threshold",
    "daily_doubletime_threshold",
    "minimum_hours",
    "rigging_minimum_hours",
  ],
  "Meal & Break Policies": ["meal_penalty_hours", "meal_penalty_rate", "meal_callback_minimum", "meal_break_duration"],
  "Cancellation Policies": ["crew_short_cancelation", "rigging_short_cancelation", "rigging_two_day_cancelation"],
  "Client Billing & Discounts": [
    "shift_differential_preferred",
    "shift_differential_regular",
    "preferred_partner_discount",
    "early_scheduling_limit",
    "early_scheduling_percentage",
  ],
  "Holiday & Premium Pay": ["premium_holiday_rate", "other_holiday_rate"],
  "Scheduling Fees & Limits": ["late_scheduling_fee", "late_scheduling_limit"],
  "Payment Terms": ["billing_net", "late_payment_penalty"],
};

// Helper to format field names for display
export const formatTermLabel = (key: string): string => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Term field metadata interface
export interface TermFieldMetadata {
  key: string;
  label: string;
  description: string;
  type: "number" | "boolean" | "string" | "enum";
  min?: number;
  max?: number;
  enumValues?: string[];
  split?: boolean;
}

// Extract term field metadata from schema
export const getTermFieldMetadata = (): Record<string, TermFieldMetadata> => {
  // Get the shape - it might be a function or an object depending on Zod version
  const shapeFn = (TermMetaSchema as any)._def.shape;
  const shape = typeof shapeFn === "function" ? shapeFn() : shapeFn;

  const fields: Record<string, TermFieldMetadata> = {};

  Object.keys(shape || {}).forEach((key) => {
    const field = shape[key];
    // Handle optional wrapper - description might be on the optional wrapper itself
    const innerField = field._def?.innerType || field;
    const zodType = innerField._def?.typeName;

    // Try to get description from both the field and innerField
    const description = field._def?.description || innerField._def?.description || "";

    // Extract constraints (min/max) from checks
    const checks = innerField._def?.checks || [];
    const minCheck = checks.find((c: any) => c.kind === "min");
    const maxCheck = checks.find((c: any) => c.kind === "max");

    // Determine field type
    let type: "number" | "boolean" | "string" | "enum" = "string";
    let enumValues: string[] | undefined;

    if (zodType === "ZodEnum") {
      type = "enum";
      enumValues = innerField._def?.values || [];
    } else if (zodType === "ZodNumber") {
      type = "number";
    } else if (zodType === "ZodBoolean") {
      type = "boolean";
    } else if (zodType === "ZodString") {
      type = "string";
    }

    // Extract split flag from UI metadata
    const split = field._def?.ui?.split || innerField._def?.ui?.split || false;

    fields[key] = {
      key,
      label: formatTermLabel(key),
      description,
      type,
      min: minCheck?.value,
      max: maxCheck?.value,
      enumValues,
      split,
    };
  });

  return fields;
};
